import { Component, computed, inject, type OnInit, signal, viewChild } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { ToastrService } from "ngx-toastr";

import { PermissionActionDescriptorType } from "../../../../adaa-types";
import { AdaaHelper, isDsV2, isDsV3 } from "../../../core/utils";
import {
  AggregatedPerformanceComponent,
  AiPerformanceComponent,
  DataTableComponent,
  EntityOverallPerformanceComponent,
  GovernmentEnablerPillarsComponent,
  PeriodSliderComponent,
} from "../../../shared/components";
import { genericFloatButtons } from "../../../shared/components/general/float-action";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, PermissionAction } from "../../../shared/constants/enums";
import { HasPermissionDirective } from "../../../shared/directives";
import type {
  AggregationWeights,
  EntityModelType,
  EntityWeights,
  EntityWeightsInfo,
  SelectedPeriodModelType,
  TableButtonClicked,
} from "../../../shared/models";
import {
  AggregationPerfService,
  AppService,
  ConsoleService,
  FavoritesApiService,
  KpisApiService,
  PillarEkpiPerformanceService,
  SystemLayoutService,
  UserService,
} from "../../../shared/services";

@Component({
  selector: "adaa-entity",
  templateUrl: "./entity.html",
  styleUrl: "./entity.scss",
  providers: [AggregationPerfService],
  imports: [
    RouterLink,
    TranslatePipe,
    DataTableComponent,
    AggregatedPerformanceComponent,
    PeriodSliderComponent,
    EntityOverallPerformanceComponent,
    GovernmentEnablerPillarsComponent,
    AiPerformanceComponent,
    HasPermissionDirective,
  ],
})
export class EntityComponent implements OnInit {
  readonly userService = inject(UserService);
  readonly consoleService = inject(ConsoleService);
  readonly systemLayoutService = inject(SystemLayoutService);
  private readonly _router = inject(Router);
  private readonly _appService = inject(AppService);
  private readonly _aggregationPerfService = inject(AggregationPerfService);
  private readonly _pillarEkpiPerformanceService = inject(PillarEkpiPerformanceService);
  private readonly _kpisApiService = inject(KpisApiService);

  private readonly _genericFloatButtons = genericFloatButtons();
  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _favoritesApiService = inject(FavoritesApiService);

  entity = signal<EntityModelType | undefined>(undefined);
  selectedPeriod = signal<SelectedPeriodModelType | undefined>(undefined);
  aggregationWeights = signal<AggregationWeights>({});
  entityWeights = signal<Record<number, { weightIssue: boolean }>>({});

  isExternalOrParticipating = computed(
    () =>
      this.entity() &&
      (this.entity()?.entityType === Constants.ENTITY_TYPES.EXTERNAL ||
        this.entity()?.entityType === Constants.ENTITY_TYPES.PARTICIPATING)
  );
  aggregationTypeCardTitle = computed(() => {
    const isAnnual = this.selectedPeriod()!.type === "year";
    if (isAnnual) {
      return this.selectedPeriod()?.period.year;
    }
    return `Q${this.selectedPeriod()?.period.quarter} ${this.selectedPeriod()?.period.year}`;
  });
  overallAggregationPerf = computed(() => this._aggregationPerfService.overallAggregationPerf());
  currentUser = computed(() => this.userService.currentUser());
  highPriorityParamString = computed(() => {
    let qs = this.selectedPeriod()?.period?.id ? `periodId=${this.selectedPeriod()?.period?.id}&` : "";
    qs += `strategicEntityId=${this.entity()?.id}&priorityHigh=Y`;
    return qs;
  });
  objectivesTableConf = computed(() => {
    const isAnnual = this.selectedPeriod()?.type === "year";
    return isAnnual
      ? Constants.MAIN_TABLE_LIST_CONF_KEY.HOMEPAGE_OBJECTIVES_ENTITIES_YEAR
      : Constants.MAIN_TABLE_LIST_CONF_KEY.HOMEPAGE_OBJECTIVES_ENTITIES;
  });
  hasKPIsWithIssue = computed(() => this._aggregationPerfService.hasKPIsWithIssue());
  highPriorityInitiativesConf = computed(() => {
    const period = this.selectedPeriod();
    if (period?.type === "year") {
      return Constants.MAIN_TABLE_LIST_CONF_KEY.HOMEPAGE_INITIATIVE_ANNUAL_HIGH_PRIORITY;
    }
    return Constants.MAIN_TABLE_LIST_CONF_KEY.HOMEPAGE_INITIATIVE_HIGH_PRIORITY;
  });
  favoriteTable = viewChild.required<DataTableComponent>("favoriteTable");

  readonly isDsV2orV3 = () => isDsV2() || isDsV3();
  readonly favoritesTableConf = Constants.MAIN_TABLE_LIST_CONF_KEY.HOMEPAGE_FAVORITES;
  readonly externalWindowStartYear = computed(() => {
    const currentYear = moment().utcOffset(Constants.uaeTimezone).year();
    const establishmentYear = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      type: "prop",
      property: "yearOfEstablishment",
    }) as number | undefined;
    const oldestYear = AdaaHelper.isDefined(establishmentYear)
      ? establishmentYear
      : Constants.DEFAULT_ENTITY_ESTABLISHMENT_YEAR;

    const anchorYear = this.selectedPeriod()?.period.year ?? currentYear;
    return Math.min(Math.max(anchorYear - 1, oldestYear), currentYear - 1);
  });
  readonly externalStartDate = computed(() =>
    moment().utcOffset(Constants.uaeTimezone).startOf("year").year(this.externalWindowStartYear()).valueOf()
  );
  readonly externalEndDate = computed(() =>
    moment()
      .utcOffset(Constants.uaeTimezone)
      .endOf("year")
      .year(this.externalWindowStartYear() + 2)
      .valueOf()
  );

  readonly objectivesParamString = () => {
    const qs = new URLSearchParams({
      enabled: AdaaBoolean.N,
      itemType: `${Constants.CONSTANT_OBJECTIVETYPE}`,
    });

    const entity = AdaaHelper.entity;
    qs.set("strategicEntityId", `${entity?.id}`);

    const periodId = this.selectedPeriod()?.period?.id;
    if (periodId) qs.set("periodId", `${periodId}`);

    return qs.toString();
  };

  readonly aiKpiPermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "or",
      permissions: [
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.AIKPI,
        },
        {
          permissionAction: PermissionAction.view,
          objectTypeId: Constants.CONSTANT_PERMISSIONS.AGENTIC_DASHBOARD,
        },
      ],
    },
  };

  public ngOnInit() {
    this.consoleService.init();
    this._refresh();
    this._updateActiveEntity();
    this._floatActions();
    if (this.entity()) {
      this._kpisApiService.getEntityWeightInfo(this.entity()!.id).subscribe({
        next: (response) => this._processEntityWeightInfo(response.responseData),
      });
    }
  }

  public goToViewObjective({
    id,
    objectiveType,
    enabler,
  }: {
    id: number;
    objectiveType: number;
    enabler: AdaaBoolean;
  }) {
    let qs = "";
    if (this.selectedPeriod()) qs = `?periodId=${this.selectedPeriod()?.period.id}`;

    if (!isDsV2()) return this._router.navigateByUrl(`/console/objectives/${id}${qs}`);
    else if (enabler === AdaaBoolean.Y && Constants.OBJECTIVE_TYPE.ENABLER_OBJECTIVE === objectiveType)
      return this._router.navigateByUrl(`/console/objectives/enabler-objective/${id}${qs}`);
    else if (enabler === AdaaBoolean.Y && Constants.OBJECTIVE_TYPE.TRANSFORMATIONAL_OBJECTIVE === objectiveType)
      return this._router.navigateByUrl(`/console/objectives/transformational-objective/${id}${qs}`);
    else if (enabler === AdaaBoolean.N && Constants.OBJECTIVE_TYPE.MAIN_OBJECTIVE === objectiveType)
      return this._router.navigateByUrl(`/console/objectives/main-objective/${id}${qs}`);

    return this._router.navigateByUrl(`/console/objectives/strategic-objective/${id}${qs}`);
  }

  public goToInitiatives({ id }: { id: number }) {
    let qs = "";
    if (this.selectedPeriod()) qs = `?periodId=${this.selectedPeriod()?.period.id}`;
    return this._router.navigateByUrl(`/console/initiatives/${id}${qs}`);
  }

  public isDeleted({ status }: { status: number }) {
    return status === Constants.OBJECT_STATUS.REMOVE;
  }

  public canUserView(fieldName: string) {
    switch (fieldName) {
      case "enablerTeam":
        return this.currentUser()?.ekpifpClassification === AdaaBoolean.Y;
      case "serviceTeam":
        return this.currentUser()?.sfpClassification === AdaaBoolean.Y;
      case "directionalTeam":
        return this.currentUser()?.dkpifpClassification === AdaaBoolean.Y;
      case "nationalTeam":
        return this.currentUser()?.nkifpClassification === AdaaBoolean.Y;
      case "skpiopm":
        return this.currentUser()?.smClassification === AdaaBoolean.Y;
      default:
        return false;
    }
  }

  public selectPeriod(data: SelectedPeriodModelType) {
    if (!data) return;
    const { period, type } = data;
    if (type === "quarter") {
      this._aggregationPerfService
        .entityQuarterlyPerformance(period.id, this.entity()!.id, this.isExternalOrParticipating())
        .subscribe();
      this._pillarEkpiPerformanceService.getData(period.id, true, this.entity()!.id).subscribe();
    } else if (type === "year") {
      this._aggregationPerfService
        .entityAnnualPerformance(period.year, this.entity()!.id, this.isExternalOrParticipating())
        .subscribe();
      this._pillarEkpiPerformanceService.getData(period.year, false, this.entity()!.id).subscribe();
    }
    this._aggregationPerfService.getCountAuditIssues(period.id, this.entity()!.id).subscribe();
    this.selectedPeriod.set({ period, type });
  }

  private _updateActiveEntity() {
    const currentEntity = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      type: "json",
    }) as EntityModelType;

    if (!currentEntity) return;

    if (currentEntity.id === Constants.CONSTANT_PMO_ID) {
      this._router.navigateByUrl("/dashboard/target");
      return;
    }

    this.entity.update(() => currentEntity);

    if (this.selectedPeriod()) {
      const isQuarter = this.selectedPeriod()?.type === "quarter";
      const period = isQuarter ? this.selectedPeriod()?.period.id : this.selectedPeriod()?.period.year;
      this._pillarEkpiPerformanceService.getData(period!, isQuarter, currentEntity.id).subscribe();
    }
  }

  private _refresh() {
    this.systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => this._appService.reloadPage("/dashboard/entity"),
    });

    this.systemLayoutService.hasCycleChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => this._appService.reloadPage("/dashboard/entity"),
    });
  }

  private _floatActions(): void {
    this._genericFloatButtons([
      {
        key: "print",
        data: undefined,
      },
    ]);
  }

  private _processEntityWeightInfo(data: Array<EntityWeightsInfo>) {
    const years = Array.from(new Set(data.map((val) => +val.year)));

    const entityWeightsYears: Record<number, { weightIssue: boolean }> = {};
    for (const year of years) {
      entityWeightsYears[+year] = {
        weightIssue:
          data
            .filter((val: EntityWeightsInfo) => val.year === +year)
            .map((val: EntityWeightsInfo) => val.weightValueInfo)
            .filter((val: EntityWeights) => val.totalWeights !== 100).length > 0,
      };
    }

    let aggTypes: number[] = [];
    const aggregationWeights: AggregationWeights = {};
    for (const item of data) {
      item.aggregationTypes = (item.aggregationTypes as string).split(",").map((type) => +type.trim());
      aggTypes = aggTypes.concat(...item.aggregationTypes);
    }

    for (const aggType of aggTypes) {
      aggregationWeights[aggType] = {
        perYear: years.reduce(
          (acc: Record<number, { weightIssue: boolean; kpiTypesWithIssues: string[] }>, year: number) => {
            if (!((+year) in acc)) {
              acc[+year] = { weightIssue: false, kpiTypesWithIssues: [] as string[] };
            }

            acc[+year] = {
              weightIssue:
                [
                  ...data
                    .filter((val: EntityWeightsInfo) => {
                      const isYearAvailable = val.year === +year;
                      const isAggTypeAvailable =
                        (val.aggregationTypes as string[]).filter((v: string) => +aggType === +v).length > 0;
                      return isYearAvailable && isAggTypeAvailable;
                    })
                    .map((val: EntityWeightsInfo) => val.weightValueInfo),
                ].filter((val) => val.totalWeights !== 100).length > 0,
              kpiTypesWithIssues: Array.from(
                new Set(
                  data
                    .filter((val: EntityWeightsInfo) => val.year === +year)
                    .map((val: EntityWeightsInfo) => val.weightValueInfo)
                    .filter((val: EntityWeights) => val.totalWeights !== 100)
                    .map((val: EntityWeights) => val.kpiType.toString())
                )
              ),
            };

            return acc;
          },
          {}
        ),
      };
    }

    this.entityWeights.set(entityWeightsYears);
    this.aggregationWeights.set(aggregationWeights);
  }

  public favoriteClicked(event: TableButtonClicked) {
    const kpiType = Constants.ITEM_TYPE_ROUTES.find((e) => e.itemTypeId === event.data.itemTypeId);
    switch (event.event) {
      case "view":
        if (kpiType) this._router.navigateByUrl(`${kpiType.route}${event.data.itemId}`);
        break;
      case "delete":
        this._deleteFavorite(event.data.id);
        break;
      default:
        break;
    }
  }

  private _deleteFavorite(id: number) {
    this._favoritesApiService.remove(id).subscribe({
      next: (res) => {
        if (res.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.favoriteTable().loadTableData(true, true);
      },
    });
  }
}
